import { getUserData } from './API';

/**
 * Grade bands for theming content based on student grade level
 */
export const GRADE_BANDS = {
  PRE: 'PRE',    // Pre-K and Kindergarten (grades -1 and 0)
  LOW: 'LOW',    // Early elementary (grades 1-2)
  MEDIUM: 'MEDIUM', // Middle elementary (grades 3-5)
  HIGH: 'HIGH'   // Upper elementary and beyond (grade 6+)
};

/**
 * Converts a student's grade to the appropriate grade band
 * @returns {string} The grade band (PRE, LOW, MEDIUM, or HIGH)
 */
export function getGradeBand() {
  const userData = getUserData();
  const grade = userData.actualGrade;

  if (grade === undefined || grade === null) {
    return GRADE_BANDS.LOW;
  }
  
  if (grade <= 0) return GRADE_BANDS.PRE;
  if (grade <= 2) return GRADE_BANDS.LOW;
  if (grade <= 5) return GRADE_BANDS.MEDIUM;
  return GRADE_BANDS.HIGH;
}

export function isPreGradeBand() {
  return getGradeBand() === GRADE_BANDS.PRE;
}

export function isLowGradeBand() {
  return getGradeBand() === GRADE_BANDS.LOW;
}

export function isMediumGradeBand() {
  return getGradeBand() === GRADE_BANDS.MEDIUM;
}

export function isHighGradeBand() {
  return getGradeBand() === GRADE_BANDS.HIGH;
}

/**
 * Returns different content based on the user's grade band
 * @param {Object} options - Object containing content for different grade bands
 * @param {*} options.pre - Content for PRE grade band
 * @param {*} options.low - Content for LOW grade band
 * @param {*} options.medium - Content for MEDIUM grade band
 * @param {*} options.high - Content for HIGH grade band
 * @param {*} options.default - Default content if no match is found
 * @returns {*} Content for the user's grade band, or default if not specified
 */
export function getContentByGradeBand({ pre, low, medium, high, default: defaultContent }) {
  const gradeBand = getGradeBand();
  
  switch (gradeBand) {
    case GRADE_BANDS.PRE:
      return pre !== undefined ? pre : defaultContent;
    case GRADE_BANDS.LOW:
      return low !== undefined ? low : defaultContent;
    case GRADE_BANDS.MEDIUM:
      return medium !== undefined ? medium : defaultContent;
    case GRADE_BANDS.HIGH:
      return high !== undefined ? high : defaultContent;
    default:
      return defaultContent;
  }
}

/**
 * Returns correct copy based on the user's grade band
 * This obfusicates getContentByGradeBand for copy, as it uses a common schema for all copy
 * WE ALWAYS ASSUME LOW IS DEFAULT CONTENT
 * @param {Object} object - The object to get the copy from
 * @param {string} defaultKey - The DEAFAULT key of the copy to get
 * @param {Object} options - Optional parameters
 * @param {Object} options.gradeOverrides - Object mapping specific grades to target grade bands
 *   e.g., { 2: 'MEDIUM' } would treat grade 2 as MEDIUM grade band
 * @returns {string} The copy for the user's grade band
 */
export function getCopyByGradeband(object, defaultKey, options = {}) {
  const { gradeOverrides = {} } = options;
  
  // Get the effective grade band considering the override
  let effectiveGradeBand = getGradeBand();
  
  // Apply grade-specific overrides
  if (Object.keys(gradeOverrides).length > 0) {
    const userData = getUserData();
    const grade = userData.actualGrade;
    
    // Check if there's an override for this specific grade
    if (grade !== undefined && grade !== null && gradeOverrides.hasOwnProperty(grade)) {
      const targetGradeBand = gradeOverrides[grade];
      
      // Validate that the target grade band is valid
      if (Object.values(GRADE_BANDS).includes(targetGradeBand)) {
        effectiveGradeBand = targetGradeBand;
      }
    }
  }
  
	var copy = null;

	// If pre, return pre if exists, default if not
	if (effectiveGradeBand === GRADE_BANDS.PRE) {
		var key = defaultKey + '_PRE_GRADE_BAND';
		copy = object[key];
	} 
  
  // If high, return high if exists
  if (effectiveGradeBand === GRADE_BANDS.HIGH) {
		copy = object[defaultKey + '_HIGH_GRADE_BAND'];
	} 
	
	// If medium or high but no high, return medium if exists
	if (effectiveGradeBand === GRADE_BANDS.MEDIUM || (effectiveGradeBand === GRADE_BANDS.HIGH && !copy)) {
		copy = object[defaultKey + '_MID_GRADE_BAND'];
	} 

	// If low, or any other grade band but no copy, return default
	if (
		effectiveGradeBand === GRADE_BANDS.LOW ||
		(!copy && (
			effectiveGradeBand === GRADE_BANDS.PRE || 
			effectiveGradeBand === GRADE_BANDS.MEDIUM || 
			effectiveGradeBand === GRADE_BANDS.HIGH
		))
	) {
		copy = object[defaultKey];
	}

	return copy;
}

export default {
  GRADE_BANDS,
  getGradeBand,
  isPreGradeBand,
  isLowGradeBand,
  isMediumGradeBand,
  isHighGradeBand,
  getContentByGradeBand,
	getCopyByGradeband,
};